import { useEffect, useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ArmsThemeProvider } from './theme/ArmsThemeProvider';
import { DialogProvider } from './components/dialogs/DialogProvider';
import { RequireAuth } from './auth/RequireAuth';
import { RootRedirect } from './auth/RootRedirect';
import { AppShell } from './components/shell/AppShell';
import { installSessionKeepalive } from './auth/sessionKeepalive';
import { installExpiryWatcher, subscribeExpiry } from './auth/sessionExpiry';

// All pages are eagerly imported (no lazy chunks): with a stable
// AppShell layout, the page content is the only thing that swaps on
// navigation, and we don't want a Suspense fallback to flash. Initial
// bundle is ~3 MB minified (~1.2 MB gzipped) — fine on dev loopback,
// acceptable on production behind Caddy with HTTP/2.
// #163 — AppShell mounts once at the layout level so the sidebar's
// scroll position survives navigation. Pages publish their title via
// usePageTitle().
import BootstrapPage from './pages/BootstrapPage';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import OperatorsPage from './pages/operators/OperatorsPage';
import SitesPage from './pages/sites/SitesPage';
import SiteControllersPage from './pages/site-controllers/SiteControllersPage';
import TerminalSessionsPage from './pages/site-controllers/TerminalSessionsPage';
import CertReissueApprovalsPage from './pages/site-controllers/CertReissueApprovalsPage';
import CaRotationPage from './pages/site-controllers/CaRotationPage';
import MachinesPage from './pages/machines/MachinesPage';
import SuppliersPage from './pages/suppliers/SuppliersPage';
import CashDeskTerminalsPage from './pages/cashdesk/CashDeskTerminalsPage';
import CashDeskOptionsPage from './pages/cashdesk/CashDeskOptionsPage';
import FloorLayoutPage from './pages/cashdesk/FloorLayoutPage';
import CashDeskActivitiesPage from './pages/cashdesk/CashDeskActivitiesPage';
import UsersPage from './pages/users/UsersPage';
import UserLoginAuditPage from './pages/users/UserLoginAuditPage';
import RolesPage from './pages/roles/RolesPage';
import PermissionsPage from './pages/permissions/PermissionsPage';
import SasAnalyzerPage from './pages/sas-analyzer/SasAnalyzerPage';
import ApprovalsPage from './pages/approvals/ApprovalsPage';
import AuditPage from './pages/audit/AuditPage';
import IssuesPage from './pages/issues/IssuesPage';
import StickerHeaderTemplatesPage from './pages/stickers/StickerHeaderTemplatesPage';
import QrBatchesPage from './pages/qr-batches/QrBatchesPage';
import ScQrBatchDetailPage from './pages/qr-batches/ScQrBatchDetailPage';
import ScQrBatchPrintPage from './pages/qr-batches/ScQrBatchPrintPage';
import IotQrBatchesPage from './pages/iot-qr-batches/IotQrBatchesPage';
import IotQrBatchDetailPage from './pages/iot-qr-batches/IotQrBatchDetailPage';
import IotQrBatchPrintPage from './pages/iot-qr-batches/IotQrBatchPrintPage';
import MachineDevicesPage from './pages/iot-qr-batches/MachineDevicesPage';
import MachineQrBatchesPage from './pages/machine-qr-batches/MachineQrBatchesPage';
import MachineQrBatchDetailPage from './pages/machine-qr-batches/MachineQrBatchDetailPage';
import MachineQrBatchPrintPage from './pages/machine-qr-batches/MachineQrBatchPrintPage';
import ReleaseCataloguePage from './pages/release-catalogue/ReleaseCataloguePage';
import SystemUserKeysPage from './pages/system-users/SystemUserKeysPage';
import AppearancePage from './pages/settings/AppearancePage';
import SystemSettingsPage from './pages/settings/SystemSettingsPage';
import OperatorBrandingPage from './pages/operator-branding/OperatorBrandingPage';
import RevenuePortfolioPage from './pages/revenue/RevenuePortfolioPage';
import RevenueSitePage from './pages/revenue/RevenueSitePage';
import RevenueMachinePage from './pages/revenue/RevenueMachinePage';

export default function App() {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            // Cache aggressively — most lookups (operators, sites,
            // machines, branding) change rarely within a session.
            // Without these defaults, TanStack treats every query as
            // immediately stale and re-fires on every component mount.
            staleTime: 30_000,        // 30 s — fresh enough for an admin UI
            gcTime:    5 * 60_000,    // keep cached results 5 min after last use
          },
        },
      }),
    [],
  );

  // Arm the eager session-expiry watcher exactly once. The watcher fires
  // when the JWT `exp` is reached; we drop the query cache so a stale
  // dashboard doesn't repaint after the redirect.
  useEffect(() => {
    installExpiryWatcher();
    installSessionKeepalive();
    const unsubscribe = subscribeExpiry(() => { queryClient.clear(); });
    return unsubscribe;
  }, [queryClient]);

  // No external auth provider — ARMS issues its own JWT and the SPA reads it
  // from sessionStorage. The /login and /setup routes mint and store the
  // token; everything else expects it to already be there.
  return (
    <QueryClientProvider client={queryClient}>
      <ArmsThemeProvider>
        <DialogProvider>
        <BrowserRouter>
          <Routes>
            {/* Public — auth-related entry points. */}
            <Route path="/"          element={<RootRedirect />} />
            <Route path="/setup"     element={<BootstrapPage />} />
            <Route path="/login"     element={<LoginPage />} />

            {/* Protected — every page below sits inside a single AppShell
                layout instance, so the sidebar's scroll position survives
                navigation (#163). RequireAuth gates the layout once. */}
            <Route element={<RequireAuth><AppShell /></RequireAuth>}>
              <Route path="/home"                  element={<HomePage />} />
              <Route path="/operators"             element={<OperatorsPage />} />
              <Route path="/sites"                 element={<SitesPage />} />
              <Route path="/site-controllers"      element={<SiteControllersPage />} />
              <Route path="/site-controllers/terminal-sessions" element={<TerminalSessionsPage />} />
              <Route path="/site-controllers/recovery-approvals" element={<CertReissueApprovalsPage />} />
              <Route path="/site-controllers/ca-rotation" element={<CaRotationPage />} />
              <Route path="/machines"              element={<MachinesPage />} />
              <Route path="/revenue"               element={<RevenuePortfolioPage />} />
              <Route path="/revenue/sites/:siteId" element={<RevenueSitePage />} />
              <Route path="/revenue/machines/:machineId" element={<RevenueMachinePage />} />
              <Route path="/suppliers"             element={<SuppliersPage />} />
              <Route path="/cashdesk/terminals"    element={<CashDeskTerminalsPage />} />
              <Route path="/cashdesk/options"      element={<CashDeskOptionsPage />} />
              <Route path="/cashdesk/floor-layout" element={<FloorLayoutPage />} />
              <Route path="/cashdesk/activities"   element={<CashDeskActivitiesPage />} />
              <Route path="/users"                 element={<UsersPage />} />
              <Route path="/roles"                 element={<RolesPage />} />
              <Route path="/permissions"           element={<PermissionsPage />} />
              <Route path="/machine-comm-analyser" element={<SasAnalyzerPage />} />
              <Route path="/approvals"             element={<ApprovalsPage />} />
              <Route path="/audit"                 element={<AuditPage />} />
              <Route path="/issues"                element={<IssuesPage />} />
              <Route path="/stickers/header-templates" element={<StickerHeaderTemplatesPage />} />
              <Route path="/factory/qr-batches"   element={<QrBatchesPage />} />
              <Route path="/factory/qr-batches/:batchId"        element={<ScQrBatchDetailPage />} />
              <Route path="/factory/qr-batches/:batchId/print"  element={<ScQrBatchPrintPage />} />
              <Route path="/factory/iot-qr-batches"                element={<IotQrBatchesPage />} />
              <Route path="/factory/iot-qr-batches/:batchId"       element={<IotQrBatchDetailPage />} />
              <Route path="/factory/iot-qr-batches/:batchId/print" element={<IotQrBatchPrintPage />} />
              <Route path="/factory/machine-qr-batches"                element={<MachineQrBatchesPage />} />
              <Route path="/factory/machine-qr-batches/:batchId"       element={<MachineQrBatchDetailPage />} />
              <Route path="/factory/machine-qr-batches/:batchId/print" element={<MachineQrBatchPrintPage />} />
              <Route path="/iot-devices"                           element={<MachineDevicesPage />} />
              <Route path="/releases/catalogue"   element={<ReleaseCataloguePage />} />
              <Route path="/admin/users/:userId/keys" element={<SystemUserKeysPage />} />
              <Route path="/users/:userId/login-audit" element={<UserLoginAuditPage />} />
              <Route path="/settings/appearance"   element={<AppearancePage />} />
              <Route path="/settings/system"       element={<SystemSettingsPage />} />
              <Route path="/operator/branding"     element={<OperatorBrandingPage />} />
            </Route>
            <Route path="*"                      element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </DialogProvider>
      </ArmsThemeProvider>
    </QueryClientProvider>
  );
}
